import { getChampionshipAnnouncedTeams } from 'modules/teams/store/championship-teams/actions';
import {
  announcedChampionshipTeamsDataSelector,
  announcedChampionshipTeamsIsLoadingSelector,
  updatingChampionshipTeamSelector,
} from 'modules/teams/store/championship-teams/selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UDText } from 'ud-ui/components';
import { AnnouncedTeamStatus } from '../../../domain/enums/announced-team-status';
import AddTeamApplicationForm from '../add-team-application-form/add-team-application-form';
import ChampionshipApplicationFilters, {
  ChampionshipApplicationFiltersOption,
  ChampionshipTeamsFilterValues,
} from '../championship-application-filters/championship-application-filters';
import ChampionshipTeamCard from './championship-team-card';
import ChampionshipTeamsSkeleton from './championship-teams-skeleton';

const statusItems: ChampionshipApplicationFiltersOption<AnnouncedTeamStatus>[] = [
  {
    status: AnnouncedTeamStatus.PENDING,
    color: '#000000',
  },
  {
    status: AnnouncedTeamStatus.ACCEPTED,
    color: '#58B879',
  },
  {
    status: AnnouncedTeamStatus.DECLINED,
    color: '#FF7061',
  },
];

type ChampionshipApplicationsProps = {
  championshipId: number;
};

const ChampionshipApplications = ({ championshipId }: ChampionshipApplicationsProps) => {
  const dispatch = useDispatch();

  const [status, selectStatus] = useState<AnnouncedTeamStatus>(AnnouncedTeamStatus.PENDING);
  const [name, setName] = useState<string>('');

  const isLoading = useSelector(announcedChampionshipTeamsIsLoadingSelector);
  const announcedTeams = useSelector(announcedChampionshipTeamsDataSelector(championshipId));
  const updatingTeamId = useSelector(updatingChampionshipTeamSelector);

  useEffect(() => {
    if (!announcedTeams) {
      dispatch(getChampionshipAnnouncedTeams({ championshipId }));
    }
  }, [championshipId, dispatch, announcedTeams]);

  const handleFilterChange = useCallback((values: ChampionshipTeamsFilterValues<AnnouncedTeamStatus>) => {
    setName(values.name);
    selectStatus(values.status);
  }, []);

  const existTeamsId = useMemo(() => {
    return Object.keys(announcedTeams || []).map((keys) => Number(keys));
  }, [announcedTeams]);

  const teamsApplicationsList = useMemo(() => {
    return Object.values(announcedTeams || [])
      .filter(
        (team) => status === team.status || (status === AnnouncedTeamStatus.PENDING && team.status === AnnouncedTeamStatus.ANNOUNCED),
      )
      .filter((team) => team.name.toLowerCase().includes(name.toLowerCase()));
  }, [announcedTeams, status, name]);

  const countByStatus = useMemo(() => {
    return Object.values(announcedTeams || []).reduce<Record<string, number>>(
      (result, item) => {
        if (item.status === AnnouncedTeamStatus.ANNOUNCED) {
          const count = result[AnnouncedTeamStatus.PENDING] || 0;
          return {
            ...result,
            [AnnouncedTeamStatus.PENDING]: count + 1,
          };
        }

        const count = result[item.status] || 0;
        return {
          ...result,
          [item.status]: count + 1,
        };
      },
      {
        [AnnouncedTeamStatus.PENDING]: 0,
        [AnnouncedTeamStatus.ACCEPTED]: 0,
        [AnnouncedTeamStatus.DECLINED]: 0,
      },
    );
  }, [announcedTeams]);

  return (
    <div className="my-4 w-full px-6">
      {isLoading ? (
        <ChampionshipTeamsSkeleton />
      ) : (
        <>
          <div className="mb-10" style={{ backgroundColor: 'white', borderRadius: 8, padding: 20 }}>
            <ChampionshipApplicationFilters<AnnouncedTeamStatus>
              selectedStatus={status}
              name={name}
              options={statusItems}
              inputPlaceholder="Поиск по команде"
              countByStatus={countByStatus}
              onChange={handleFilterChange}
            />
          </div>

          <AddTeamApplicationForm existTeamsId={existTeamsId} championshipId={championshipId} />

          <div>
            {teamsApplicationsList.length === 0 ? (
              <div className="flex-center h-full">
                <UDText type="body-text" className="text-surfaceRomanSilver30">
                  Список пуст
                </UDText>
              </div>
            ) : (
              teamsApplicationsList.map((team, index) => (
                <ChampionshipTeamCard
                  key={`${team.id}-${team.name}`}
                  index={index + 1}
                  championshipTeam={team}
                  championshipId={championshipId}
                  isUpdating={updatingTeamId === team.id}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChampionshipApplications;
